import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api/client";
import { useLanguage } from "../../contexts/LanguageContext";
import logger from "../../utils/logger.js";
import {
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  Download,
  Eye,
} from "lucide-react";
import "./MyInvoices.css";

export default function MyInvoices() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const labels = {
    en: {
      title: "My Invoices",
      loading: "Loading invoices...",
      loadError: "Failed to load invoices.",
      empty: "You don't have any invoices yet.",
      colIndex: "#",
      colInvoiceNumber: "Invoice No.",
      colReference: "Reference",
      colType: "Type",
      colTotal: "Total",
      colStatus: "Status",
      colPayment: "Payment",
      colDate: "Date",
      btnView: "View",
      btnPdf: "PDF",
      statusCompleted: "Completed",
      statusProcessing: "Processing",
      statusCanceled: "Canceled",
      statusPending: "Pending",
    },
    ar: {
      title: "فواتيري",
      loading: "جارِ تحميل الفواتير...",
      loadError: "فشل تحميل الفواتير.",
      empty: "لا توجد فواتير حتى الآن.",
      colIndex: "#",
      colInvoiceNumber: "رقم الفاتورة",
      colReference: "المرجع",
      colType: "النوع",
      colTotal: "الإجمالي",
      colStatus: "الحالة",
      colPayment: "الدفع",
      colDate: "التاريخ",
      btnView: "عرض",
      btnPdf: "PDF",
      statusCompleted: "مكتملة",
      statusProcessing: "قيد المعالجة",
      statusCanceled: "ملغاة",
      statusPending: "معلقة",
    },
  }[lang];

  function renderStatusBadge(statusRaw) {
    const s = (statusRaw || "").toLowerCase();
    let statusClass = "pending";
    let text = statusRaw || "";

    switch (s) {
      case "completed":
        statusClass = "completed";
        text = labels.statusCompleted;
        break;
      case "processing":
        statusClass = "processing";
        text = labels.statusProcessing;
        break;
      case "canceled":
      case "cancelled":
        statusClass = "canceled";
        text = labels.statusCanceled;
        break;
      case "pending":
      default:
        statusClass = "pending";
        text = labels.statusPending;
        break;
    }

    return (
      <span className={`invoices-status-badge ${statusClass}`}>{text}</span>
    );
  }

  function getStatusIcon(statusRaw) {
    const s = (statusRaw || "").toLowerCase();
    switch (s) {
      case "completed":
        return <CheckCircle size={14} />;
      case "processing":
        return <Clock size={14} />;
      case "canceled":
      case "cancelled":
        return <AlertTriangle size={14} />;
      case "pending":
      default:
        return <Clock size={14} />;
    }
  }

  function paymentMethodText(method) {
    const m = (method || "").toLowerCase();
    if (!m) return "—";

    if (m === "cod") return isAr ? "الدفع عند الاستلام" : "Cash on delivery";
    if (m === "card" || m === "stripe" || m === "stripe_card")
      return isAr ? "بطاقة (Stripe)" : "Card (Stripe)";
    if (m === "paypal") return "PayPal";
    if (m === "bank_transfer") return isAr ? "تحويل بنكي" : "Bank transfer";
    if (m === "wallet") return isAr ? "محفظة إلكترونية" : "Wallet";

    return method;
  }

  function invoiceTypeText(type) {
    const normalized = (type || "order").toLowerCase();

    if (normalized === "course") {
      return isAr ? "كورس" : "Course";
    }

    if (normalized === "service") {
      return isAr ? "خدمة" : "Service";
    }

    return isAr ? "طلب" : "Order";
  }

  function referenceText(invoice) {
    if (invoice.reference_title) {
      return invoice.reference_id
        ? `${invoice.reference_title} (#${invoice.reference_id})`
        : invoice.reference_title;
    }

    if (invoice.order_id) {
      return `#${invoice.order_id}`;
    }

    if (invoice.reference_id) {
      return `#${invoice.reference_id}`;
    }

    return "—";
  }

  function formatDate(value) {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString(isAr ? "ar" : "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return String(value);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function loadInvoices() {
      setLoading(true);
      setError("");

      try {
        const res = await api.apiGet("invoices");

        if (!mounted) return;

        if (res?.status === "success" && Array.isArray(res.invoices)) {
          setInvoices(res.invoices);
        } else {
          setError(res?.message || labels.loadError);
        }
      } catch (err) {
        logger.error("loadInvoices error", err);
        if (mounted) {
          setError(labels.loadError);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadInvoices();

    return () => {
      mounted = false;
    };
  }, [labels.loadError]);

  return (
    <div className="invoices-container" dir={isAr ? "rtl" : "ltr"}>
      <div className="invoices-header">
        <div className="invoices-header-icon">
          <FileText size={20} strokeWidth={2} />
        </div>
        <h2 className="invoices-header-title">{labels.title}</h2>
      </div>

      {loading && (
        <div className="invoices-message loading">
          <div className="invoices-message-icon">
            <Clock size={18} />
          </div>
          <div className="invoices-loading-text">{labels.loading}</div>
        </div>
      )}

      {!loading && error && (
        <div className="invoices-message error">
          <div className="invoices-message-icon">
            <AlertCircle size={18} />
          </div>
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && invoices.length === 0 && (
        <div className="invoices-message empty">
          <div className="invoices-message-icon">
            <FileText size={28} />
          </div>
          <p className="invoices-empty-text">{labels.empty}</p>
        </div>
      )}

      {!loading && !error && invoices.length > 0 && (
        <div className="invoices-table-wrapper">
          <div className="invoices-table-scroll">
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>{labels.colIndex}</th>
                  <th>{labels.colInvoiceNumber}</th>
                  <th>{labels.colType}</th>
                  <th>{labels.colReference}</th>
                  <th>{labels.colTotal}</th>
                  <th>{labels.colStatus}</th>
                  <th>{labels.colPayment}</th>
                  <th>{labels.colDate}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, index) => {
                  const date =
                    inv.invoice_date || inv.order_date || inv.created_at;

                  const rawPdf = inv.pdf_url || "";
                  const pdfUrl = rawPdf.replace(
                    "https://mousagroup.nethttps://",
                    "https://"
                  );

                  return (
                    <tr key={inv.id}>
                      <td>
                        <span className="invoices-row-index">{index + 1}</span>
                      </td>
                      <td>
                        <span className="invoices-invoice-number">
                          {inv.invoice_number}
                        </span>
                      </td>
                      <td>
                        <span className="invoices-type-badge">
                          {invoiceTypeText(inv.invoice_type)}
                        </span>
                      </td>
                      <td>{referenceText(inv)}</td>
                      <td>
                        <span className="invoices-total">
                          ${Number(inv.total || 0).toFixed(2)}
                        </span>
                      </td>
                      <td>{renderStatusBadge(inv.status)}</td>
                      <td>{paymentMethodText(inv.payment_method)}</td>
                      <td>{formatDate(date)}</td>
                      <td>
                        <div className="invoices-actions">
                          <Link
                            to={`/invoice/${inv.id}`}
                            className="invoices-btn invoices-btn-view"
                            title={labels.btnView}
                          >
                            <Eye size={14} />
                            <span>{labels.btnView}</span>
                          </Link>
                          {pdfUrl && (
                            <a
                              href={pdfUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="invoices-btn invoices-btn-pdf"
                              title={labels.btnPdf}
                            >
                              <Download size={14} />
                              <span>{labels.btnPdf}</span>
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
